import React, { useContext, useEffect, useState, useRef, Fragment } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    FormGroup,
    Label,
    Input,
    Table,
    Badge,
    InputGroup,
    InputGroupAddon,
    Collapse,
} from 'reactstrap';
import { Icon } from '@iconify/react';
import arrowsVertical from '@iconify/icons-carbon/arrows-vertical';
import arrowUp from '@iconify/icons-carbon/arrow-up';
import SimpleBar from 'simplebar-react';
import searchIcon from '@iconify/icons-mdi/magnify';
import closeThick from '@iconify/icons-mdi/close-thick';
import arrowDown from '@iconify/icons-carbon/arrow-down';
import filterVariant from '@iconify/icons-mdi/filter-variant';
import { useHistory, useLocation } from 'react-router-dom';
import { SrxApi } from '../api/SrxApi';
import Barcode from 'react-barcode';
import ReactToPrint from 'react-to-print';
import FlatPicker from 'react-flatpickr';
import 'flatpickr/dist/themes/dark.css';
import { SelectedFacilitiesContext } from '../contexts/SelectedFacilitiesContext';
import { FaCheck, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import checkBold from '@iconify/icons-mdi/check-bold';
import Dropdown from '../components/Dropdown';

const PackingSlips = () => {
    const PAGE_SIZE = 100;
    const getDefaultDateRange = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const previousDay = new Date(today);
        previousDay.setDate(today.getDate() - 1);
        previousDay.setHours(0, 0, 0, 0);
        return [previousDay, today];
    };
    const formatDateForApi = (date) => date.toLocaleDateString('en-US');
    const [defaultDateRange] = useState(() => getDefaultDateRange());
    const [defaultFromDate, defaultToDate] = defaultDateRange.map(formatDateForApi);
    const [totes, setTotes] = useState([]);
    const [activeTote, setActiveTote] = useState({ facID: null, toteID: null });
    const [toteDetailed, setToteDetailed] = useState(null);
    const [bulkToteDetails, setBulkToteDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [isPrinting, setIsPrinting] = useState(false);
    const [isBulkPrinting, setIsBulkPrinting] = useState(false);
    const [isFetchingBulkDetails, setIsFetchingBulkDetails] = useState(false);
    const [selectedTotes, setSelectedTotes] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [currentFilters, setCurrentFilters] = useState({
        fromDate: defaultFromDate,
        toDate: defaultToDate,
        search: undefined,
    });
    const [totalRows, setTotalRows] = useState(0);
    let history = useHistory();
    let location = useLocation();
    const datePickerRef = useRef();
    const { selectedFacilities, facilities } = useContext(SelectedFacilitiesContext);

    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [advancedFacID, setAdvancedFacID] = useState('');
    const [advancedPatientName, setAdvancedPatientName] = useState('');
    const [advancedDrugName, setAdvancedDrugName] = useState('');
    const [advancedRxNumber, setAdvancedRxNumber] = useState('');
    const [isAdvancedSearching, setIsAdvancedSearching] = useState(false);
    const [isAdvancedSearchActive, setIsAdvancedSearchActive] = useState(false);
    const [activeAdvancedFilters, setActiveAdvancedFilters] = useState(null);

    const hasSingleFacility = (facilities || []).length === 1;
    const canUseAdvancedFilters = hasSingleFacility || !!advancedFacID;
    const facilityOptions = (facilities || [])
        .filter((fac) => fac?.facName != null && fac?.facID != null)
        .map((fac) => ({
            label: `(${fac.facID})  ${fac.facName}`,
            value: fac.facID,
        }));

    const resetAdvancedFilters = () => {
        setIsAdvancedOpen(false);
        setAdvancedFacID('');
        setAdvancedPatientName('');
        setAdvancedDrugName('');
        setAdvancedRxNumber('');
        setActiveAdvancedFilters(null);
        setIsAdvancedSearchActive(false);
    };

    const getTotes = (fromDate, toDate, searchText, pageNumber = 1, selectedFacilitiesList) => {
        resetAdvancedFilters();
        setLoading(true);
        const query = new URLSearchParams();
        query.set('pageSize', PAGE_SIZE);
        query.set('pageNumber', pageNumber);
        if (fromDate && toDate) {
            query.set('fdate', fromDate);
            query.set('edate', toDate);
        }
        if (searchText) {
            query.set('SearchText', searchText);
        }
        if (selectedFacilitiesList && selectedFacilitiesList.length > 0) {
            query.set('facIDs', selectedFacilitiesList.map((faci) => faci.facID).join(','));
        }
        SrxApi.get(`/packingslipheaders?${query.toString()}`)
            .then((res) => {
                const headers = Array.isArray(res?.data?.packingSlipHeadersList)
                    ? res.data.packingSlipHeadersList
                    : Array.isArray(res?.data?.items)
                      ? res.data.items
                      : [];
                setTotes(headers);
                setTotalRows(res?.data?.totalRows ?? res?.data?.totalCount ?? headers.length);
                setCurrentPage(pageNumber);
                setSelectedTotes([]);
            })
            .catch((err) => {
                console.log(err);
                setTotes([]);
                setTotalRows(0);
            })
            .finally(() => setLoading(false));
    };

    const runAdvancedSearch = (pageNumber = 1, filtersOverride, dateRangeOverride) => {
        const activeFilters = filtersOverride || activeAdvancedFilters;
        if (!activeFilters || !activeFilters.facID) return;
        setIsAdvancedSearchActive(true);
        setIsAdvancedSearching(true);
        const effectiveDateRange = dateRangeOverride || dateRange;
        const query = new URLSearchParams();
        query.set('pageSize', PAGE_SIZE);
        query.set('pageNumber', pageNumber);
        query.set('facID', activeFilters.facID);
        if (activeFilters.patientName) query.set('patientName', activeFilters.patientName);
        if (activeFilters.drugName) query.set('drugName', activeFilters.drugName);
        if (activeFilters.rxNumber) query.set('rxNumber', activeFilters.rxNumber);
        if (effectiveDateRange && effectiveDateRange.length === 2) {
            query.set('fdate', effectiveDateRange[0].toLocaleDateString('en-US'));
            query.set('edate', effectiveDateRange[1].toLocaleDateString('en-US'));
        }
        SrxApi.get(`/packingslipheadersearch?${query.toString()}`)
            .then((res) => {
                const headers = Array.isArray(res?.data?.packingSlipHeadersList)
                    ? res.data.packingSlipHeadersList
                    : Array.isArray(res?.data?.items)
                      ? res.data.items
                      : [];
                setTotes(headers);
                setTotalRows(res?.data?.totalRows ?? res?.data?.totalCount ?? headers.length);
                setCurrentPage(pageNumber);
                setSelectedTotes([]);
            })
            .catch((err) => {
                console.log(err);
                setTotes([]);
                setTotalRows(0);
            })
            .finally(() => setIsAdvancedSearching(false));
    };

    const handleApplyAdvancedSearch = () => {
        const facID = hasSingleFacility ? facilities[0]?.facID : advancedFacID;
        if (!facID) return;
        const filters = {
            facID,
            patientName: advancedPatientName.trim() || undefined,
            drugName: advancedDrugName.trim() || undefined,
            rxNumber: advancedRxNumber.trim() || undefined,
        };
        const effectiveDateRange = dateRange && dateRange.length === 2 ? dateRange : defaultDateRange;
        setSearch('');
        setDateRange(effectiveDateRange);
        setCurrentFilters({ fromDate: undefined, toDate: undefined, search: undefined });
        setActiveAdvancedFilters(filters);
        runAdvancedSearch(1, filters, effectiveDateRange);
    };

    const handleResetAdvancedSearch = () => {
        setAdvancedFacID('');
        setAdvancedPatientName('');
        setAdvancedDrugName('');
        setAdvancedRxNumber('');
        setActiveAdvancedFilters(null);
        setIsAdvancedSearchActive(false);
        setDateRange(defaultDateRange);
        setCurrentFilters({ fromDate: defaultFromDate, toDate: defaultToDate, search: undefined });
        getTotes(defaultFromDate, defaultToDate, undefined, 1, selectedFacilities);
    };

    const handleToggleAdvancedSearch = () => {
        setIsAdvancedOpen((prev) => !prev);
    };

    const handleAdvancedSearchClosed = () => {
        if (isAdvancedSearchActive || advancedFacID || advancedPatientName || advancedDrugName || advancedRxNumber) {
            handleResetAdvancedSearch();
        }
    };

    const getToteDetails = async (facID, toteID) => {
        if (isLoadingDetails) return;

        setIsLoadingDetails(true);

        try {
            // SrxApi.get(`/packingslipdetails?facID=${'LICC'}&toteID=${'C2052260'}`)
            const res = await SrxApi.get(`/packingslipdetails?facID=${facID}&toteID=${toteID}`);

            setToteDetailed(res);
        } catch (err) {
            console.log(err);
        } finally {
            setIsLoadingDetails(false);
        }
    };
    useEffect(() => {
        setSearch('');
        setDateRange(defaultDateRange);
        setCurrentFilters({
            fromDate: defaultFromDate,
            toDate: defaultToDate,
            search: undefined,
        });
        getTotes(defaultFromDate, defaultToDate, undefined, 1, selectedFacilities);
    }, [selectedFacilities]);

    // useEffect(() => {
    //     const query = new URLSearchParams(location.search);

    //     let facID = query.get('facID');
    //     let toteID = query.get('toteID');
    //     if (toteID && facID) {
    //         setActiveTote({ facID, toteID });
    //     } else {
    //         setActiveTote({ faciID: null, toteID: null })
    //         setToteDetailed(null)
    //     }

    // }, [location.search])

    // useEffect(() => {
    //     if (activeTote.facID && activeTote.toteID) {
    //         getToteDetails();
    //     } else {
    //         return
    //     }
    // }, [activeTote])

    const getFormattedDate = (date) => {
        let onlyDate = new Date(date).toLocaleDateString();
        return onlyDate;
    };

    const handleEnter = (e) => {
        if (e.key === 'Enter') {
            if (!search || search.trim() === '') {
                if (
                    currentFilters.search !== undefined ||
                    currentFilters.fromDate !== defaultFromDate ||
                    currentFilters.toDate !== defaultToDate
                ) {
                    setDateRange(defaultDateRange);
                    setCurrentFilters({ fromDate: defaultFromDate, toDate: defaultToDate, search: undefined });
                    getTotes(defaultFromDate, defaultToDate, undefined, 1, selectedFacilities);
                }
            } else {
                if (currentFilters.search !== search) {
                    if (datePickerRef.current && datePickerRef.current.flatpickr) {
                        datePickerRef.current.flatpickr.clear();
                    }
                    setDateRange(null);
                    setCurrentFilters({ fromDate: undefined, toDate: undefined, search });
                    getTotes(undefined, undefined, search, 1, undefined);
                }
            }
        }
    };

    const getToteSelectionKey = (tote) => `${tote?.facID ?? ''}::${tote?.toteID ?? ''}`;

    const toggleToteSelection = (tote) => {
        const toteKey = getToteSelectionKey(tote);
        setSelectedTotes((prev) =>
            prev.some((item) => getToteSelectionKey(item) === toteKey)
                ? prev.filter((item) => getToteSelectionKey(item) !== toteKey)
                : [...prev, tote]
        );
    };

    const toggleSelectAllOnPage = () => {
        const allSelectedOnPage =
            totes.length > 0 &&
            totes.every((tote) =>
                selectedTotes.some((selected) => getToteSelectionKey(selected) === getToteSelectionKey(tote))
            );
        if (allSelectedOnPage) {
            setSelectedTotes((prev) =>
                prev.filter((item) => !totes.some((tote) => getToteSelectionKey(tote) === getToteSelectionKey(item)))
            );
            return;
        }

        const nextSelection = [...selectedTotes];
        totes.forEach((tote) => {
            if (!nextSelection.some((item) => getToteSelectionKey(item) === getToteSelectionKey(tote))) {
                nextSelection.push(tote);
            }
        });
        setSelectedTotes(nextSelection);
    };

    const fetchBulkToteDetails = async () => {
        if (!selectedTotes.length || isFetchingBulkDetails) {
            return;
        }
        setIsFetchingBulkDetails(true);
        try {
            const responseList = await Promise.all(
                selectedTotes.map((tote) =>
                    SrxApi.get(`/packingslipdetails?facID=${tote.facID}&toteID=${tote.toteID}`)
                        .then((res) => ({
                            tote,
                            data: Array.isArray(res?.data)
                                ? res.data
                                : Array.isArray(res?.data?.items)
                                  ? res.data.items
                                  : [],
                        }))
                        .catch(() => ({ tote, data: [] }))
                )
            );
            const printableData = responseList.filter((item) => Array.isArray(item.data) && item.data.length > 0);
            setBulkToteDetails(printableData);
        } finally {
            setIsFetchingBulkDetails(false);
        }
    };

    function searchFacilities(array) {
        const list = Array.isArray(array) ? [...array] : [];
        list.sort((a, b) => (a.name < b.name ? -1 : 1));
        if (selectedFacilities?.length > 0 && !currentFilters.search) {
            let results = list.filter((slip) => selectedFacilities.some((faci) => slip.facID === faci.facID));
            //let results = array.filter(faci => faci.facName.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()));
            return results;
        } else {
            return list;
        }
    }

    const [sortBy, setSortBy] = useState({
        sortField: 'facName',
        sortDirection: 'asc',
    });

    function sortByField(array) {
        switch (sortBy.sortField) {
            case 'nsName':
                if (sortBy.sortDirection === 'asc') return array.sort((a, b) => (a.nsName < b.nsName ? -1 : 1));
                else return array.sort((a, b) => (a.nsName > b.nsName ? -1 : 1));
            case 'patCount':
                if (sortBy.sortDirection === 'asc') return array.sort((a, b) => (a.patCount < b.patCount ? -1 : 1));
                else return array.sort((a, b) => (a.patCount > b.patCount ? -1 : 1));
            case 'rxCount':
                if (sortBy.sortDirection === 'asc') return array.sort((a, b) => (a.rxCount < b.rxCount ? -1 : 1));
                else return array.sort((a, b) => (a.rxCount > b.rxCount ? -1 : 1));
            case 'shipDate':
                if (sortBy.sortDirection === 'asc')
                    return array.sort((a, b) => (new Date(a.shipDate) > new Date(b.shipDate) ? -1 : 1));
                else return array.sort((a, b) => (new Date(a.shipDate) < new Date(b.shipDate) ? -1 : 1));
            case 'facName':
                if (sortBy.sortDirection === 'asc') return array.sort((a, b) => (a.facName < b.facName ? -1 : 1));
                else return array.sort((a, b) => (a.facName > b.facName ? -1 : 1));
            case 'toteID':
                if (sortBy.sortDirection === 'asc') return array.sort((a, b) => (a.toteID < b.toteID ? -1 : 1));
                else return array.sort((a, b) => (a.toteID > b.toteID ? -1 : 1));
            case 'facID':
                if (sortBy.sortDirection === 'asc') return array.sort((a, b) => (a.facID < b.facID ? -1 : 1));
                else return array.sort((a, b) => (a.facID > b.facID ? -1 : 1));
            case 'delivered':
                if (sortBy.sortDirection === 'asc') return array.sort((a, b) => (a.delivered < b.delivered ? -1 : 1));
                else return array.sort((a, b) => (a.delivered > b.delivered ? -1 : 1));

            case 'pharmID':
                if (sortBy.sortDirection === 'asc') return array.sort((a, b) => (a.pharmID < b.pharmID ? -1 : 1));
                else return array.sort((a, b) => (a.pharmID > b.pharmID ? -1 : 1));
        }
    }

    function onClickSort(field) {
        if (field === sortBy.sortField) {
            setSortBy({
                ...sortBy,
                sortDirection: sortBy.sortDirection === 'asc' ? 'desc' : 'asc',
            });
        } else {
            setSortBy({
                sortField: field,
                sortDirection: 'asc',
            });
        }
    }

    const SortCarets = () => (
        <>
            {sortBy.sortDirection === 'asc' ? (
                <Icon icon={arrowUp} color="black" />
            ) : (
                <Icon icon={arrowDown} color="black" />
            )}
        </>
    );

    const filteredAndSortedTotes = sortByField(searchFacilities(totes)) || [];
    const totalPages = Math.max(1, Math.ceil((totalRows || 0) / PAGE_SIZE));
    const pageStart = (currentPage - 1) * PAGE_SIZE;

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    if (!loading)
        return (
            <>
                <div className="mt-3 mx-2">
                    <h2>Totes</h2>
                    <Card>
                        <CardBody>
                            <div
                                className="d-flex flex-wrap justify-content-between align-items-center mb-2"
                                style={{ rowGap: '12px', columnGap: '12px' }}>
                                {/* Date Range group */}
                                <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                                    <FormGroup
                                        className="mb-0 d-flex flex-wrap align-items-center"
                                        style={{ gap: '8px' }}>
                                        <Label
                                            className="d-flex align-items-center mb-0"
                                            style={{ whiteSpace: 'nowrap' }}>
                                            Date Range:{' '}
                                        </Label>
                                        <InputGroup style={{ width: '260px', maxWidth: '100%' }}>
                                            <FlatPicker
                                                ref={datePickerRef}
                                                className="form-control rounded-left bg-white"
                                                value={dateRange ?? null}
                                                options={{
                                                    onChange: (selectedDates, dateStr, instance) =>
                                                        setDateRange(selectedDates),
                                                    maxDate: 'today',
                                                    // maxDate: archivedData.dateRange.to,
                                                    mode: 'range',
                                                    altInput: true,
                                                    altFormat: 'm-d-Y',
                                                    // altFormat: "F j, Y",
                                                    dateFormat: 'Y-m-d',
                                                }}
                                                placeholder="Choose date..."></FlatPicker>
                                            <InputGroupAddon addonType="append">
                                                <span
                                                    onClick={() => {
                                                        if (
                                                            currentFilters.fromDate !== undefined ||
                                                            currentFilters.toDate !== undefined ||
                                                            currentFilters.search !== undefined
                                                        ) {
                                                            datePickerRef.current.flatpickr.clear();
                                                            setCurrentFilters({
                                                                fromDate: undefined,
                                                                toDate: undefined,
                                                                search: undefined,
                                                            });
                                                            getTotes(
                                                                undefined,
                                                                undefined,
                                                                undefined,
                                                                1,
                                                                selectedFacilities
                                                            );
                                                        }
                                                    }}
                                                    role="button"
                                                    className="input-group-text py-0 px-1 bg-white">
                                                    <Icon icon={closeThick} width="25px" />
                                                </span>
                                                <span
                                                    onClick={() => {
                                                        setSearch('');
                                                        if (!dateRange || dateRange.length < 2) {
                                                            return;
                                                        }
                                                        const fromDate = dateRange[0].toLocaleDateString('en-US');
                                                        const toDate = dateRange[1].toLocaleDateString('en-US');
                                                        if (
                                                            currentFilters.fromDate !== fromDate ||
                                                            currentFilters.toDate !== toDate ||
                                                            currentFilters.search !== undefined
                                                        ) {
                                                            setCurrentFilters({
                                                                fromDate,
                                                                toDate,
                                                                search: undefined,
                                                            });
                                                            getTotes(
                                                                fromDate,
                                                                toDate,
                                                                undefined,
                                                                1,
                                                                selectedFacilities
                                                            );
                                                        }
                                                    }}
                                                    role="button"
                                                    className="input-group-text py-0 px-1">
                                                    <Icon icon={searchIcon} width="25px" />
                                                </span>
                                            </InputGroupAddon>
                                        </InputGroup>
                                    </FormGroup>
                                </div>

                                {/* Search + Advanced Search group */}
                                <div className="d-flex flex-wrap align-items-center" style={{ gap: '12px' }}>
                                    <FormGroup
                                        className="mb-0"
                                        style={{
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            width: '260px',
                                            maxWidth: '100%',
                                        }}>
                                        <InputGroup>
                                            <Input
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                onKeyUp={(e) => handleEnter(e)}
                                                disabled={isAdvancedSearching}
                                                placeholder={`Search TOT... PAK...`}></Input>
                                            <InputGroupAddon addonType="append">
                                                <span
                                                    onClick={() => {
                                                        if (
                                                            search !== '' ||
                                                            currentFilters.search !== undefined ||
                                                            currentFilters.fromDate !== defaultFromDate ||
                                                            currentFilters.toDate !== defaultToDate
                                                        ) {
                                                            setSearch('');
                                                            setDateRange(defaultDateRange);
                                                            setCurrentFilters({
                                                                fromDate: defaultFromDate,
                                                                toDate: defaultToDate,
                                                                search: undefined,
                                                            });
                                                            getTotes(
                                                                defaultFromDate,
                                                                defaultToDate,
                                                                undefined,
                                                                1,
                                                                selectedFacilities
                                                            );
                                                        }
                                                    }}
                                                    role="button"
                                                    className="input-group-text py-0 px-1 bg-white">
                                                    <Icon icon={closeThick} width="25px" />
                                                </span>
                                                <span
                                                    onClick={() => {
                                                        if (!search || search.trim() === '') {
                                                            if (
                                                                currentFilters.search !== undefined ||
                                                                currentFilters.fromDate !== defaultFromDate ||
                                                                currentFilters.toDate !== defaultToDate
                                                            ) {
                                                                setDateRange(defaultDateRange);
                                                                setCurrentFilters({
                                                                    fromDate: defaultFromDate,
                                                                    toDate: defaultToDate,
                                                                    search: undefined,
                                                                });
                                                                getTotes(
                                                                    defaultFromDate,
                                                                    defaultToDate,
                                                                    undefined,
                                                                    1,
                                                                    selectedFacilities
                                                                );
                                                            }
                                                        } else {
                                                            if (currentFilters.search !== search) {
                                                                if (
                                                                    datePickerRef.current &&
                                                                    datePickerRef.current.flatpickr
                                                                ) {
                                                                    datePickerRef.current.flatpickr.clear();
                                                                }
                                                                setDateRange(null);
                                                                setCurrentFilters({
                                                                    fromDate: undefined,
                                                                    toDate: undefined,
                                                                    search,
                                                                });
                                                                getTotes(undefined, undefined, search, 1, undefined);
                                                            }
                                                        }
                                                    }}
                                                    role="button"
                                                    className="input-group-text py-0 px-1">
                                                    <Icon icon={searchIcon} width="25px" />
                                                </span>
                                            </InputGroupAddon>
                                        </InputGroup>
                                    </FormGroup>

                                    <Button
                                        color="primary"
                                        outline={!isAdvancedOpen}
                                        size="md"
                                        className="flex-shrink-0"
                                        onClick={handleToggleAdvancedSearch}>
                                        <Icon icon={filterVariant} className="mr-1" />
                                        Advanced Search{' '}
                                        {isAdvancedOpen ? (
                                            <FaChevronUp className="ml-1" />
                                        ) : (
                                            <FaChevronDown className="ml-1" />
                                        )}
                                    </Button>
                                </div>

                    
                            </div>

                            <div className="mb-3">
                                <Collapse isOpen={isAdvancedOpen} onExited={handleAdvancedSearchClosed}>
                                    <div
                                        className="border rounded p-3"
                                        style={{ background: '#f7f5fb', borderColor: '#e4defa' }}>
                                        {!hasSingleFacility && (
                                            <>
                                                <FormGroup className="mb-2">
                                                    <Label className="mb-1">
                                                        <b>Facility Name</b> <span className="text-danger">*</span>{' '}
                                                        <span className="text-muted" style={{ fontWeight: 'normal' }}>
                                                            (required)
                                                        </span>
                                                    </Label>
                                                    <Row>
                                                        <Col md={5}>
                                                            <Dropdown
                                                                options={facilityOptions}
                                                                placeholder="— Select a facility —"
                                                                selectedDropdownValue={advancedFacID}
                                                                setSelectedDropdownValue={setAdvancedFacID}
                                                            />
                                                        </Col>
                                                        <Col
                                                            md={7}
                                                            className="d-flex align-items-center text-muted"
                                                            style={{ fontSize: '13px' }}>
                                                            Select a facility to enable additional filters.
                                                        </Col>
                                                    </Row>
                                                </FormGroup>
                                                <hr />
                                            </>
                                        )}
                                        <Label
                                            className="mb-2 text-muted d-block"
                                            style={{ fontSize: '12px', letterSpacing: '0.5px' }}>
                                            <b>ADDITIONAL FILTERS</b>
                                        </Label>
                                        <Row>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label className="mb-1" style={{ fontSize: '12px' }}>
                                                        PATIENT NAME
                                                    </Label>
                                                    <Input
                                                        value={advancedPatientName}
                                                        onChange={(e) => setAdvancedPatientName(e.target.value)}
                                                        placeholder="Last or first name"
                                                        disabled={!canUseAdvancedFilters}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label className="mb-1" style={{ fontSize: '12px' }}>
                                                        DRUG NAME
                                                    </Label>
                                                    <Input
                                                        value={advancedDrugName}
                                                        onChange={(e) => setAdvancedDrugName(e.target.value)}
                                                        placeholder="e.g. Metoprolol"
                                                        disabled={!canUseAdvancedFilters}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label className="mb-1" style={{ fontSize: '12px' }}>
                                                        RX NUMBER
                                                    </Label>
                                                    <Input
                                                        value={advancedRxNumber}
                                                        onChange={(e) => setAdvancedRxNumber(e.target.value)}
                                                        placeholder="e.g. 123456"
                                                        disabled={!canUseAdvancedFilters}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <div>
                                            <Button
                                                color="primary"
                                                className="mr-2"
                                                disabled={!canUseAdvancedFilters || isAdvancedSearching}
                                                onClick={handleApplyAdvancedSearch}>
                                                {isAdvancedSearching ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm mr-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                        Searching...
                                                    </>
                                                ) : (
                                                    'Apply'
                                                )}
                                            </Button>
                                            <Button
                                                color="secondary"
                                                outline
                                                disabled={isAdvancedSearching}
                                                onClick={handleResetAdvancedSearch}>
                                                Reset
                                            </Button>
                                        </div>
                                    </div>
                                </Collapse>
                            </div>
                            <div style={{ position: 'relative' }} className="bg-white rounded pt-3">
                                {isAdvancedSearching && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'rgba(255,255,255,0.7)',
                                            zIndex: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        <div className="text-center">
                                            <div className="mb-1">
                                                <b>Searching...</b>
                                            </div>
                                            <div className="bouncing-loader">
                                                <div></div>
                                                <div></div>
                                                <div></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {!isAdvancedSearching && !filteredAndSortedTotes.length ? (
                                    <div style={{ minHeight: '33vh' }}>
                                        <h2 className="text-center fw-bold mt-5">Nothing to show</h2>
                                    </div>
                                ) : (
                                    <SimpleBar style={{ height: '565px' }}>
                                        <Table className="mb-0">
                                            <thead className="bg-light" style={{ position: 'sticky', top: '-1px' }}>
                                                <tr>
                                                    <th className="text-center" style={{ width: '42px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={
                                                                totes.length > 0 &&
                                                                totes.every((tote) =>
                                                                    selectedTotes.some(
                                                                        (selected) =>
                                                                            getToteSelectionKey(selected) ===
                                                                            getToteSelectionKey(tote)
                                                                    )
                                                                )
                                                            }
                                                            onChange={toggleSelectAllOnPage}
                                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                        />
                                                    </th>
                                                    <th onClick={() => onClickSort('facID')} className="th-hover">
                                                        Facilty ID{' '}
                                                        {sortBy.sortField === 'facID' ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )}
                                                    </th>
                                                    <th onClick={() => onClickSort('facName')} className="th-hover">
                                                        Facility Name{' '}
                                                        {sortBy.sortField === 'facName' ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )}
                                                    </th>
                                                    <th onClick={() => onClickSort('pharmID')} className="th-hover">
                                                        Pharmacy ID{' '}
                                                        {sortBy.sortField === 'pharmID' ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )}
                                                    </th>
                                                    <th onClick={() => onClickSort('nsName')} className="th-hover">
                                                        NS Name{' '}
                                                        {sortBy.sortField === 'nsName' ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )}
                                                    </th>
                                                    <th onClick={() => onClickSort('toteID')} className="th-hover">
                                                        Tote ID{' '}
                                                        {sortBy.sortField === 'toteID' ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )}
                                                    </th>
                                                    <th onClick={() => onClickSort('shipDate')} className="th-hover">
                                                        Ship Date{' '}
                                                        {sortBy.sortField === 'shipDate' ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )}
                                                    </th>
                                                    <th onClick={() => onClickSort('patCount')} className="th-hover">
                                                        Pat. Count{' '}
                                                        {sortBy.sortField === 'patCount' ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )}
                                                    </th>
                                                    <th onClick={() => onClickSort('rxCount')} className="th-hover">
                                                        RX Count{' '}
                                                        {sortBy.sortField === 'rxCount' ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )}
                                                    </th>
                                                    <th onClick={() => onClickSort('delivered')} className="th-hover">
                                                        Delivered{' '}
                                                        {sortBy.sortField === 'delivered' ? (
                                                            <SortCarets />
                                                        ) : (
                                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                                        )}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredAndSortedTotes.map((tote, index) => {
                                                    return (
                                                        <tr
                                                            style={{ cursor: 'pointer' }}
                                                            key={index}
                                                            // onClick={() => {
                                                            //     getToteDetails(tote.facID, tote.toteID);
                                                            //     const newLocation = {
                                                            //         pathname: location.pathname,
                                                            //         search: `facID=${tote.facID}&toteID=${tote.toteID}`,
                                                            //     };

                                                            //     history.push(newLocation);
                                                            // }}
                                                            onClick={async () => {
                                                                if (isLoadingDetails) return;

                                                                const newLocation = {
                                                                    pathname: location.pathname,
                                                                    search: `facID=${tote.facID}&toteID=${tote.toteID}`,
                                                                };

                                                                history.push(newLocation);

                                                                await getToteDetails(tote.facID, tote.toteID);
                                                            }}>
                                                            <td
                                                                style={{ verticalAlign: 'middle' }}
                                                                onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedTotes.some(
                                                                        (item) =>
                                                                            getToteSelectionKey(item) ===
                                                                            getToteSelectionKey(tote)
                                                                    )}
                                                                    onChange={() => toggleToteSelection(tote)}
                                                                    style={{
                                                                        width: '16px',
                                                                        height: '16px',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                />
                                                            </td>
                                                            <th scope="row" style={{ verticalAlign: 'middle' }}>
                                                                {tote.facID}
                                                            </th>
                                                            <td style={{ verticalAlign: 'middle' }}>{tote.facName}</td>
                                                            <td style={{ verticalAlign: 'middle' }}>{tote.pharmID}</td>
                                                            <td style={{ verticalAlign: 'middle' }}>{tote.nsName} </td>
                                                            <td style={{ verticalAlign: 'middle' }}>{tote.toteID} </td>
                                                            <td style={{ verticalAlign: 'middle' }}>
                                                                {getFormattedDate(tote.shipDate)}
                                                            </td>
                                                            <td style={{ verticalAlign: 'middle' }}>{tote.patCount}</td>
                                                            <td style={{ verticalAlign: 'middle' }}>{tote.rxCount} </td>
                                                            <td
                                                                style={{
                                                                    verticalAlign: 'middle',
                                                                    textAlign: 'center',
                                                                }}>
                                                                {tote.delivered ? (
                                                                    <Badge
                                                                        style={{
                                                                            width: '22px',
                                                                            height: '22px',
                                                                            textAlign: 'center',
                                                                            display: 'flex',
                                                                            justifyContent: 'center',
                                                                            alignItems: 'center',
                                                                        }}
                                                                        color="success">
                                                                        <Icon
                                                                            style={{
                                                                                color: 'white',
                                                                                fontSize: '20px',
                                                                            }}
                                                                            icon={checkBold}
                                                                        />
                                                                    </Badge>
                                                                ) : (
                                                                    <Fragment />
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </SimpleBar>
                                )}
                            </div>
                            {!!filteredAndSortedTotes.length && (
                                <div className="d-flex justify-content-between align-items-center mt-2">
                                    <span>
                                        Showing {pageStart + 1}-
                                        {Math.min(pageStart + filteredAndSortedTotes.length, totalRows)} of {totalRows}
                                    </span>
                                    <div>
                                        <Button
                                            size="sm"
                                            color="primary"
                                            className="mr-2"
                                            disabled={!selectedTotes.length || isFetchingBulkDetails}
                                            onClick={fetchBulkToteDetails}>
                                            {isFetchingBulkDetails
                                                ? 'Preparing...'
                                                : `Print Selected (${selectedTotes.length})`}
                                        </Button>
                                        <Button
                                            size="sm"
                                            color="secondary"
                                            className="mr-2"
                                            disabled={currentPage === 1}
                                            onClick={() =>
                                                isAdvancedSearchActive
                                                    ? runAdvancedSearch(currentPage - 1)
                                                    : getTotes(
                                                          currentFilters.fromDate,
                                                          currentFilters.toDate,
                                                          currentFilters.search,
                                                          currentPage - 1,
                                                          currentFilters.search ? undefined : selectedFacilities
                                                      )
                                            }>
                                            Previous
                                        </Button>
                                        <Button
                                            size="sm"
                                            color="secondary"
                                            disabled={currentPage >= totalPages}
                                            onClick={() =>
                                                isAdvancedSearchActive
                                                    ? runAdvancedSearch(currentPage + 1)
                                                    : getTotes(
                                                          currentFilters.fromDate,
                                                          currentFilters.toDate,
                                                          currentFilters.search,
                                                          currentPage + 1,
                                                          currentFilters.search ? undefined : selectedFacilities
                                                      )
                                            }>
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                    {isLoadingDetails && (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                background: 'rgba(255,255,255,0.6)',
                                zIndex: 99999,
                            }}>
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '55%',
                                    transform: 'translate(-50%, -50%)',
                                    textAlign: 'center',
                                }}>
                                <h4 className="mb-1">Loading...</h4>

                                <div className="bouncing-loader">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                            </div>
                        </div>
                    )}
                    <ToteDetailModal
                        tote={toteDetailed}
                        isPrinting={isPrinting}
                        setIsPrinting={setIsPrinting}
                        toggle={() => {
                            setToteDetailed(null);
                            const newLocation = {
                                pathname: location.pathname,
                            };
                            history.push(newLocation);
                        }}
                    />
                    <BulkTotePrintModal
                        toteDetails={bulkToteDetails}
                        isPrinting={isBulkPrinting}
                        setIsPrinting={setIsBulkPrinting}
                        toggle={() => setBulkToteDetails([])}
                    />
                </div>
            </>
        );

    return (
        <div className="page-loading-loader-div w-100 d-flex justify-content-center align-items-center">
            <div>
                <h4>Loading Totes...</h4>
                <div className="bouncing-loader">
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            </div>
        </div>
    );
};

const SlipPrintBody = ({ detailData, toteID, isPrinting }) => {
    const getFormattedDate = (date) => {
        let onlyDate = new Date(date).toLocaleDateString();
        return onlyDate;
    };
    const detailItems = Array.isArray(detailData)
        ? detailData
        : Array.isArray(detailData?.items)
          ? detailData.items
          : [];
    const normalizedDetailData = detailItems.flatMap((x) => (Array.isArray(x) ? x : [x]));
    function getDateAndTimeStamp(date) {
        let DateAndTime = new Date(date).toLocaleTimeString([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
        return DateAndTime;
    }
    return (
        <>
            {normalizedDetailData.map((nsID, index) => {
                return (
                    <div
                        key={`${index}-${nsID?.psid ?? 'slip'}-${nsID?.nsID ?? ''}`}
                        className="single-slip"
                        style={{ borderBottom: '1px dashed #dee2e6', marginBottom: '1.75rem' }}>
                        <div>
                            <h4 className="mt-0">
                                <span className="mb-0 pl-1 pr-3">
                                    NS:{' '}
                                    <b className="mr-3">
                                        {nsID.nsName} ({nsID.nsID})
                                    </b>
                                    Date: <b className="mr-4">{getFormattedDate(nsID.shipDate)}</b>
                                    Tote ID: <b>{toteID}</b>
                                    <b className="float-right bg-light px-1 rounded">Packing Slip</b>
                                </span>
                            </h4>
                        </div>
                        <div className="bg-secondary text-white pl-1">Deliver To:</div>
                        <div className="border py-1 mb-1">
                            <Row>
                                <Col>
                                    <p className="mb-0 pl-1">
                                        <b>
                                            {nsID.facName} ({nsID.facID})
                                        </b>
                                    </p>
                                    <p className="mb-0 pl-1">
                                        <b>
                                            {nsID.facStreet1} <br />
                                            {nsID.facCity} {nsID.facState} {nsID.facZip}
                                        </b>
                                    </p>
                                    <p className="mb-0 pl-1">
                                        Delivery Route: <b>{nsID.deliveryRoute ?? '-'}</b>
                                    </p>
                                    <p className="mb-0 pl-1">
                                        {/* <Barcode fontSize='15' width='1' height='50' value={`TOT${t?.toteID}`} /> */}
                                        <Barcode
                                            fontSize={isPrinting ? '17' : '15'}
                                            width={isPrinting ? '2' : '1'}
                                            height={isPrinting ? '100' : '50'}
                                            value={`${nsID?.toteID ?? toteID}`}
                                        />
                                    </p>
                                </Col>
                                <Col className="border-left">
                                    <p className="mb-0">
                                        <b>
                                            {nsID.pharmName} ({nsID.pharmID})
                                        </b>
                                    </p>
                                    <p className="mb-0">
                                        <b>
                                            {nsID.pharmStreet1} <br />
                                            {nsID.pharmCity} {nsID.pharmState} {nsID.pharmZip}
                                        </b>
                                    </p>
                                    <p className="mb-0">
                                        <b>{nsID.pharmPhone}</b>
                                    </p>
                                    <p className="mb-0 pl-1 align-right">
                                        {/* <Barcode fontSize='15' width='1' height='50' value={`PAK${t.psid}`} /> */}
                                        <Barcode
                                            fontSize={isPrinting ? '17' : '15'}
                                            width={isPrinting ? '2' : '1'}
                                            height={isPrinting ? '100' : '50'}
                                            value={`PAK${nsID?.psid ?? ''}`}
                                        />
                                    </p>
                                </Col>
                            </Row>
                        </div>
                        {nsID?.legalHeader && (
                            <div className="text-center">
                                <h3>{nsID?.legalHeader}</h3>
                            </div>
                        )}
                        <div>
                            {nsID?.patientDetails?.map((slip, slipIndex) => {
                                return (
                                    <div className="" key={`${slip?.patID ?? slipIndex}-${slipIndex}`}>
                                        <div className="pl-1 mt-0">
                                            <b className="mr-2">{slip.patName} </b>
                                            <span className="mr-2">({slip.patID})</span>
                                            Rm:<b className="mr-2">{slip.room}</b>
                                            Bed:<b>{slip.bed}</b>
                                        </div>
                                        <div className="pl-1 pt-0 patient-drug-details mb-4">
                                            {slip?.drugDetails?.map((d, drugIndex) => {
                                                return (
                                                    <div key={`${d.rxNo ?? drugIndex}-${drugIndex}`}>
                                                        <div className="single-drug">
                                                            <Row className="drug-info-line">
                                                                <Col xs={3} className="pl-2">
                                                                    <div className="pl-2">
                                                                        Rx:<b className="mr-1">{d.rxNo}</b> Ro:
                                                                        <b>{d.roNo}</b>
                                                                    </div>
                                                                </Col>
                                                                <Col xs={5}>
                                                                    <b>
                                                                        {d.deaClass?.trim()?.length ? (
                                                                            <Badge
                                                                                title="dea class"
                                                                                className="bg-danger mr-1 dea-class"
                                                                                pill>
                                                                                {d?.deaClass}
                                                                            </Badge>
                                                                        ) : null}
                                                                        Qty: <b>{d.qty}</b> &nbsp; - &nbsp;
                                                                        {d.drugLabelName}
                                                                    </b>
                                                                </Col>
                                                                <Col xs={4}>
                                                                    By:<b>{d.phName}</b>
                                                                    {nsID.showDeliveryInfo && (
                                                                        <span className="float-right mr-1">
                                                                            <Badge
                                                                                title="delivery status"
                                                                                className={`delivery-status badge-outline-${d.delivered ? 'success' : 'warning'}`}
                                                                                pill>
                                                                                {d.delivered
                                                                                    ? 'Delivered'
                                                                                    : 'Not delivered'}
                                                                            </Badge>
                                                                        </span>
                                                                    )}
                                                                </Col>
                                                            </Row>
                                                            <Row>
                                                                <Col className="pl-2">
                                                                    <div className="pl-2 drug-note">
                                                                        {d.newRx && <b className="mr-1">{d.newRx}</b>}
                                                                        {d.serialNo && (
                                                                            <>
                                                                                Serial No:
                                                                                <Badge
                                                                                    title="serial number"
                                                                                    className="bg-secondary mx-1 dea-class"
                                                                                    pill>
                                                                                    {d.serialNo}
                                                                                </Badge>
                                                                            </>
                                                                        )}
                                                                        {d.packSlipMsg
                                                                            ? `*** ${d.packSlipMsg} ***`
                                                                            : ''}
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {nsID.showDeliveryInfo && (
                            <div className="border mb-2">
                                <Row className="">
                                    <Col xs={6} className="pl-3">
                                        <p className="mt-1 mb-0">
                                            Driver: <b>{nsID.deliveredBy ?? '-'}</b>
                                        </p>
                                        <p className="my-1">
                                            Received By: <b>{nsID.receivedBy ?? '-'}</b>
                                            {nsID.receivedDateTime ? (
                                                <span>
                                                    {' '}
                                                    on <b>{getDateAndTimeStamp(nsID.receivedDateTime)}</b>
                                                </span>
                                            ) : (
                                                ''
                                            )}
                                        </p>
                                        <p className="mb-1">
                                            Signature Title: <b>{nsID.signorTitle ?? '-'}</b>
                                        </p>
                                    </Col>

                                    <Col xs={6} className="mt-auto mb-1">
                                        <span className="mr-1 align-bottom">Signature: </span>
                                        <span>
                                            {nsID.signature && (
                                                <img
                                                    style={{ objectFit: 'contain ' }}
                                                    className="border-bottom mr-1"
                                                    width="auto"
                                                    height="60"
                                                    src={`data:image/png;base64,${nsID.signature}`}
                                                    alt="received by signature"
                                                />
                                            )}
                                        </span>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
};

const ToteDetailModal = ({ tote, toggle, isPrinting, setIsPrinting }) => {
    const printRef = useRef();
    const toteId =
        tote?.data?.[0]?.[0]?.toteID ??
        tote?.data?.[0]?.toteID ??
        tote?.data?.toteID ??
        tote?.data?.items?.[0]?.[0]?.toteID ??
        tote?.data?.items?.[0]?.toteID ??
        '';

    const handleBeforeGetContent = () => {
        setIsPrinting(true);
        return Promise.resolve();
    };

    return (
        <Modal style={{ maxWidth: '900px', width: '900px' }} centered isOpen={tote} toggle={toggle}>
            {tote ? (
                <>
                    <ModalHeader
                        className="gradient-modal my-auto"
                        toggle={toggle}
                        style={{
                            background: 'linear-gradient(90deg, #452f69 0%, #452f69 50%, #000 90%)',
                            color: 'white',
                        }}>
                        Tote ID: {toteId}
                        <ReactToPrint
                            trigger={() => (
                                <i
                                    role="button"
                                    style={{ fontSize: '20px' }}
                                    title="Print"
                                    className="mdi mdi-printer py-0 px-0 ml-2"></i>
                            )}
                            content={() => printRef.current}
                            documentTitle={`Tote ID #TOT${toteId}`}
                            onBeforeGetContent={() => handleBeforeGetContent()}
                            onAfterPrint={() => setIsPrinting(false)}
                        />
                    </ModalHeader>
                    <ModalBody>
                        <div id="printDiv" ref={printRef}>
                            <SlipPrintBody detailData={tote?.data} toteID={toteId} isPrinting={isPrinting} />
                        </div>
                    </ModalBody>
                </>
            ) : (
                ''
            )}
        </Modal>
    );
};

const BulkTotePrintModal = ({ toteDetails, toggle, isPrinting, setIsPrinting }) => {
    const printRef = useRef();
    const handleBeforeGetContent = () => {
        setIsPrinting(true);
        return Promise.resolve();
    };

    return (
        <Modal style={{ maxWidth: '900px', width: '900px' }} centered isOpen={toteDetails?.length > 0} toggle={toggle}>
            {toteDetails?.length > 0 ? (
                <>
                    <ModalHeader
                        className="gradient-modal my-auto"
                        toggle={toggle}
                        style={{
                            background: 'linear-gradient(90deg, #452f69 0%, #452f69 50%, #000 90%)',
                            color: 'white',
                        }}>
                        Selected Packing Slips ({toteDetails.length})
                        <ReactToPrint
                            trigger={() => (
                                <i
                                    role="button"
                                    style={{ fontSize: '20px' }}
                                    title="Print"
                                    className="mdi mdi-printer py-0 px-0 ml-2"></i>
                            )}
                            content={() => printRef.current}
                            documentTitle={`Selected Packing Slips`}
                            onBeforeGetContent={() => handleBeforeGetContent()}
                            onAfterPrint={() => setIsPrinting(false)}
                        />
                    </ModalHeader>
                    <ModalBody>
                        <div id="bulkPrintDiv" ref={printRef}>
                            {toteDetails.map((item) => (
                                <div key={`${item?.tote?.facID}-${item?.tote?.toteID}`}>
                                    <SlipPrintBody
                                        detailData={item?.data}
                                        toteID={item?.tote?.toteID}
                                        isPrinting={isPrinting}
                                    />
                                </div>
                            ))}
                        </div>
                    </ModalBody>
                </>
            ) : (
                ''
            )}
        </Modal>
    );
};

export default PackingSlips;
